import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Casino as CasinoIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { matkaAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';

const MatkaGameRates = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [gameTypes, setGameTypes] = useState({});
  const [gameModes, setGameModes] = useState({});
  
  const [formData, setFormData] = useState({
    game_type: '',
    game_type_id: '',
    rate: '',
    min_amount: 10,
    max_amount: 10000,
  });

  const gameTypesList = {
    1: "SINGLE_DIGIT",
    2: "SINGLE_DIGIT_BULK", 
    3: "JODI_DIGIT",
    4: "JODI_DIGIT_BULK",
    5: "SINGLE_PANNA",
    6: "SINGLE_PANNA_BULK",
    7: "DOUBLE_PANNA", 
    8: "DOUBLE_PANNA_BULK",
    9: "TRIPLE_PANNA",
    10: "TRIPLE_PANNA_BULK",
    11: "HALF_SANGAM",
    12: "FULL_SANGAM",
    13: "RED_JODI"
  };

  const gameDescriptions = {
    1: "Single digit betting (0-9)",
    2: "Multiple single digit betting",
    3: "Two digit combination (Jodi)",
    4: "Multiple Jodi combinations",
    5: "Three digit with all different numbers",
    6: "Multiple Single Panna combinations",
    7: "Three digit with 2 same numbers",
    8: "Multiple Double Panna combinations",
    9: "Three digit with all same numbers",
    10: "Multiple Triple Panna combinations",
    11: "Half Sangam betting",
    12: "Full Sangam betting",
    13: "Special red Jodi betting"
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await matkaAPI.getMatkaRates();
      if (response.data.success) {
        setRates(response.data.rates || []);
        setGameTypes(response.data.gameTypes || gameTypesList);
        setGameModes(response.data.gameModes || {});
      } else {
        toast.error('Failed to fetch Matka game rates');
      }
    } catch (err) {
      console.error('Error fetching Matka rates:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch Matka game rates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.game_type || !formData.game_type_id || !formData.rate) {
        toast.error('Please fill all required fields');
        return;
      }

      const payload = {
        ...formData,
        rate: parseFloat(formData.rate),
        min_amount: parseFloat(formData.min_amount),
        max_amount: parseFloat(formData.max_amount),
      };

      let response;
      if (selectedRate) {
        response = await matkaAPI.updateMatkaRate(selectedRate.id, payload);
      } else {
        response = await matkaAPI.createMatkaRate(payload);
      }

      if (response.data.success) {
        toast.success(selectedRate ? 'Rate updated successfully' : 'Rate created successfully');
        fetchRates();
        handleCloseDialog();
      } else {
        toast.error(response.data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error saving rate:', err);
      toast.error(err.response?.data?.message || 'Failed to save rate');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await matkaAPI.deleteMatkaRate(selectedRate.id);
      if (response.data.success) {
        toast.success('Rate deleted successfully');
        fetchRates();
        setDeleteDialogOpen(false);
        setSelectedRate(null);
      } else {
        toast.error('Failed to delete rate');
      }
    } catch (err) {
      console.error('Error deleting rate:', err);
      toast.error(err.response?.data?.message || 'Failed to delete rate');
    }
  };

  const handleOpenDialog = (rate = null) => {
    setSelectedRate(rate);
    if (rate) {
      setFormData({
        game_type: rate.game_type || '',
        game_type_id: rate.game_type_id || '',
        rate: rate.rate || '',
        min_amount: rate.min_amount || 10,
        max_amount: rate.max_amount || 10000,
      });
    } else {
      setFormData({
        game_type: '',
        game_type_id: '',
        rate: '',
        min_amount: 10,
        max_amount: 10000,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRate(null);
    setFormData({
      game_type: '',
      game_type_id: '',
      rate: '',
      min_amount: 10,
      max_amount: 10000,
    });
  };

  const handleGameTypeChange = (gameTypeId) => {
    const gameTypeName = gameTypesList[gameTypeId];
    setFormData({
      ...formData,
      game_type_id: gameTypeId,
      game_type: gameTypeName
    });
  };

  const getGameTypeChip = (gameTypeId, gameType) => {
    const colors = {
      1: 'primary', 2: 'primary', 3: 'secondary', 4: 'secondary',
      5: 'success', 6: 'success', 7: 'warning', 8: 'warning',
      9: 'error', 10: 'error', 11: 'info', 12: 'info', 13: 'default'
    };
    
    return (
      <Chip
        label={`${gameTypeId}: ${gameType}`}
        color={colors[gameTypeId] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  // Statistics
  const totalRates = rates.length;
  const availableGameTypes = Object.keys(gameTypesList).length;
  const configuredTypes = rates.length;
  const averageRate = rates.length > 0 
    ? rates.reduce((sum, rate) => sum + parseFloat(rate.rate), 0) / rates.length 
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <CasinoIcon sx={{ mr: 2 }} />
            Matka Game Rates
          </Typography>
          {(user?.user_type === 'admin' || user?.user_type === 'owner') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
              }}
            >
              Add Rate
            </Button>
          )}
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Total Rates</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {totalRates}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Configured</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {configuredTypes}/{availableGameTypes}
                      </Typography>
                    </Box>
                    <CasinoIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Average Rate</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {averageRate.toFixed(1)}x
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Game Types</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {availableGameTypes}
                      </Typography>
                    </Box>
                    <CasinoIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Rates Table */}
        <Card>
          <CardContent>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Game Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Rate (x)</TableCell>
                    <TableCell align="center">Min Amount</TableCell>
                    <TableCell align="center">Max Amount</TableCell>
                    {!isMobile && <TableCell>Created By</TableCell>}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rates.map((rate) => (
                    <TableRow key={rate.id} hover>
                      <TableCell>
                        {getGameTypeChip(rate.game_type_id, rate.game_type)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {gameDescriptions[rate.game_type_id]}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            bgcolor: 'primary.light',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1
                          }}
                        >
                          {rate.rate}x
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: 'success.main' }}>
                          {formatCurrency(rate.min_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                          {formatCurrency(rate.max_amount)}
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Typography variant="body2">
                            {rate.creator?.username || 'System'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          {(user?.user_type === 'admin' || user?.user_type === 'owner') && (
                            <>
                              <Tooltip title="Edit Rate">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(rate)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              
                              {user?.user_type === 'owner' && (
                                <Tooltip title="Delete Rate">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedRate(rate);
                                      setDeleteDialogOpen(true);
                                    }}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {rates.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" sx={{ py: 4 }}>
                          No game rates configured yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedRate ? 'Edit Matka Game Rate' : 'Add New Matka Game Rate'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Game Type"
                  select
                  value={formData.game_type_id}
                  onChange={(e) => handleGameTypeChange(e.target.value)}
                  required
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return <em style={{ color: '#999', fontStyle: 'normal' }}>Select Game Type</em>;
                      }
                      const gameTypeName = gameTypesList[selected];
                      return `${selected}: ${gameTypeName}`;
                    }
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select Game Type</em>
                  </MenuItem>
                  {Object.entries(gameTypesList).map(([id, name]) => (
                    <MenuItem key={id} value={id}>
                      {id}: {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rate (Multiplier)"
                  type="number"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  required
                  inputProps={{ min: 1, step: 0.1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Amount"
                  type="number"
                  value={formData.min_amount}
                  onChange={(e) => setFormData({ ...formData, min_amount: e.target.value })}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Amount"
                  type="number"
                  value={formData.max_amount}
                  onChange={(e) => setFormData({ ...formData, max_amount: e.target.value })}
                  required
                  inputProps={{ min: 10 }}
                />
              </Grid>

              {formData.game_type_id && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2,  borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Game Description:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {gameDescriptions[formData.game_type_id]}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedRate ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the rate for {selectedRate?.game_type}?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default MatkaGameRates;